import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";

const UpdateSummaryContent = ({
  updateSummary,
  showHeader = true,
  "data-testid": dataTestId = "update-summary-content",
}) => {
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { colors = {} } = useTheme();
  const { t } = useTranslation();

  if (!updateSummary) return null;

  const isSuccess = updateSummary.final_status === "success";
  const statusColor = isSuccess ? colors.textSuccessBright : colors.textFailureBright;
  const statusIcon = isSuccess ? "✓" : "✗";

  return (
    <div className="space-y-6" data-testid={dataTestId}>
      {showHeader && (
        <div className="flex items-center gap-3" data-testid={`${dataTestId}-header`}>
          <div className={`text-2xl ${statusColor}`} data-testid={fieldValueTestId("status-icon")}>
            {statusIcon}
          </div>
          <div>
            <h2 className={`text-lg font-bold ${colors.textPrimary}`} data-testid={`${dataTestId}-title`}>
              {t("update-summary.title")}
            </h2>
            <p className={`text-sm ${colors.textSecondary} mt-1`} data-testid={fieldValueTestId("subtitle")}>
              {t("update-summary.subtitle")}
            </p>
          </div>
        </div>
      )}

      <div className={`p-4 rounded-lg border ${colors.borderPrimary} ${colors.bgInput}`} data-testid={fieldValueTestId("status-section")}>
        <h3 className={`text-sm font-semibold ${colors.textPrimary} mb-3`}>
          {t("update-summary.status")}
        </h3>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${statusColor}`} data-testid={fieldValueTestId("status")}>
            {isSuccess ? t("update-summary.success") : t("update-summary.failed")}
          </span>
          <span className={`text-xs ${colors.textSecondary}`} data-testid={fieldValueTestId("timestamp")}>
            {updateSummary.timestamp}
          </span>
        </div>
        {updateSummary.message && (
          <p className={`text-sm ${colors.textSecondary} mt-2`} data-testid={fieldValueTestId("message")}>
            {updateSummary.message}
          </p>
        )}
      </div>

      {updateSummary.updated_services && Object.keys(updateSummary.updated_services).length > 0 && (
        <div className={`p-4 rounded-lg border ${colors.borderPrimary} ${colors.bgInput}`} data-testid={fieldValueTestId("services-section")}>
          <h3 className={`text-sm font-semibold ${colors.textPrimary} mb-3`}>
            {t("update-summary.service-update-status")}
          </h3>
          <div className="space-y-3">
            {Object.entries(updateSummary.updated_services).map(([service, versions]) => (
              <div key={service} className="flex items-center justify-between" data-testid={fieldValueTestId(`service-${service}`)}>
                <span className={`text-sm font-medium ${colors.textPrimary} capitalize`}>
                  {service}
                </span>
                {isSuccess ? (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${colors.bgSecondary} ${colors.textSecondary}`}>
                      {versions.from}
                    </span>
                    <span className={`text-xs ${colors.textSecondary}`}>→</span>
                    <span className={`text-xs px-2 py-1 rounded ${colors.bgSuccessLight} ${colors.textSuccessDark}`}>
                      {versions.to}
                    </span>
                  </div>
                ) : (
                  <span className={`text-xs px-2 py-1 rounded ${colors.bgFailureLight} ${colors.textFailureDark}`}>
                    {t("update-summary.failed")}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={`p-4 rounded-lg border ${colors.borderPrimary} ${colors.bgInput}`} data-testid={fieldValueTestId("additional-info-section")}>
        <h3 className={`text-sm font-semibold ${colors.textPrimary} mb-3`}>
          {t("update-summary.additional-info")}
        </h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className={`text-sm ${colors.textSecondary}`}>
              {t("update-summary.update-time")}:
            </span>
            <span className={`text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("update-time")}>
              {updateSummary.timestamp}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={`text-sm ${colors.textSecondary}`}>
              {t("update-summary.status")}:
            </span>
            <span className={`text-sm font-medium ${statusColor}`} data-testid={fieldValueTestId("completion-status")}>
              {isSuccess ? t("update-summary.completed") : t("update-summary.failed")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateSummaryContent;
